import React, { useMemo, useState } from "react";
import { Row, Col, Card, CardBody, ButtonGroup, Button } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import moment from "moment";
import "moment/locale/es";
import { formatCurrency } from "../../utils/formatCurrency";
import KpiCards from "./KpiCards";
import PeriodComparison from "./PeriodComparison";
import ExpandableChart from "./ExpandableChart";
import { CategorySummary, useCategories } from "./CategoryBreakdown";
import { esDiaEnCurso, sinDiaEnCurso } from "./partialDay";
import { TOOLBAR_DESCARGA, LEYENDA_DONA, SIN_LEYENDA, Referencia } from "./graficos";
import { TITULO_CARD, TITULO_ACLARACION } from "./tituloCard";
// Misma paleta que el resto de la pantalla: el color se pide por concepto.
import { INTENSIDAD, MODALIDAD, POLARIDAD, paletaLibre } from "../Stats2/theme";

const fmt = (v) => formatCurrency(v ?? 0, true);

// ── Auto-generated insights ──────────────────────────────────
const Insights = ({ dailyDocs, liveData }) => {
  const insights = useMemo(() => {
    if (!dailyDocs.length) return [];
    const result = [];
    // Promedios, rachas, proyecciones y "mejor día" se calculan SIN el día en
    // curso: es parcial y compararlo contra días completos da conclusiones
    // falsas ("tu peor día es hoy" a las 9 de la mañana).
    const completos = sinDiaEnCurso(dailyDocs);
    if (!completos.length) return [];
    const last30 = completos.slice(0, 30);
    const prev30 = completos.slice(30, 60);

    // Best day of week
    const dayBuckets = {};
    last30.forEach((d) => {
      const dow = moment(d.date).isoWeekday();
      if (!dayBuckets[dow]) dayBuckets[dow] = [];
      dayBuckets[dow].push(d.income || 0);
    });
    let bestDay = null;
    let bestAvg = 0;
    let worstDay = null;
    let worstAvg = Infinity;
    Object.entries(dayBuckets).forEach(([dow, vals]) => {
      const avg = vals.reduce((a, b) => a + b, 0) / vals.length;
      if (avg > bestAvg) {
        bestAvg = avg;
        bestDay = parseInt(dow);
      }
      if (avg < worstAvg) {
        worstAvg = avg;
        worstDay = parseInt(dow);
      }
    });
    if (bestDay) {
      const dayName = moment().isoWeekday(bestDay).format("dddd");
      result.push({
        icon: "emoji_events",
        color: "success",
        text: `Tu mejor día es ${dayName}: te deja ${fmt(bestAvg)} en promedio`,
      });
    }

    // Worst day of week (only if different from best and enough data)
    if (worstDay && worstDay !== bestDay && Object.keys(dayBuckets).length >= 5) {
      const dayName = moment().isoWeekday(worstDay).format("dddd");
      result.push({
        icon: "trending_down",
        color: "secondary",
        text: `Tu día más tranquilo es ${dayName}: te deja ${fmt(worstAvg)} en promedio`,
      });
    }

    // Income growth
    if (prev30.length >= 20) {
      const l30 = last30.reduce((a, d) => a + (d.income || 0), 0);
      const p30 = prev30.reduce((a, d) => a + (d.income || 0), 0);
      if (p30 > 0) {
        const growth = ((l30 - p30) / p30) * 100;
        result.push({
          icon: growth >= 0 ? "trending_up" : "trending_down",
          color: growth >= 0 ? "success" : "danger",
          text: growth >= 0
            ? `Los ingresos crecieron +${growth.toFixed(1)}% respecto al período anterior`
            : `Los ingresos bajaron ${growth.toFixed(1)}% respecto al período anterior`,
        });
      }
    }

    // Weekend vs weekday comparison
    const weekdays = last30.filter((d) => moment(d.date).isoWeekday() <= 5);
    const weekends = last30.filter((d) => moment(d.date).isoWeekday() > 5);
    if (weekdays.length >= 5 && weekends.length >= 2) {
      const wdAvg = weekdays.reduce((a, d) => a + (d.income || 0), 0) / weekdays.length;
      const weAvg = weekends.reduce((a, d) => a + (d.income || 0), 0) / weekends.length;
      if (wdAvg > 0) {
        const diff = ((weAvg - wdAvg) / wdAvg) * 100;
        result.push({
          icon: "weekend",
          color: diff >= 0 ? "info" : "secondary",
          text: diff >= 0
            ? `Los fines de semana te dejan ${diff.toFixed(0)}% más que los días de semana (${fmt(weAvg)} contra ${fmt(wdAvg)})`
            : `Los días de semana te dejan ${Math.abs(diff).toFixed(0)}% más que los fines de semana (${fmt(wdAvg)} contra ${fmt(weAvg)})`,
        });
      }
    }

    // Monthly projection — sobre días COMPLETOS. Incluir el día en curso
    // dividiría por un día que todavía no termino de facturar y proyectaría de
    // menos toda la mañana.
    const today = moment();
    const thisMonthDays = last30.filter((d) => d.date?.startsWith(today.format("YYYY-MM")));
    if (thisMonthDays.length >= 3) {
      const monthIncome = thisMonthDays.reduce((a, d) => a + (d.income || 0), 0);
      const daysInMonth = today.daysInMonth();
      const projection = (monthIncome / thisMonthDays.length) * daysInMonth;
      result.push({
        icon: "query_stats",
        color: "primary",
        text: `Si el mes sigue así vas a cerrar ${today.format("MMMM")} en ${fmt(projection)} estimado (sobre ${thisMonthDays.length} días ya cerrados)`,
      });
    }

    // Revenue per parking space
    const capacity = liveData?.occupancy?.capacity;
    if (capacity > 0 && last30.length >= 7) {
      const dailyAvg = last30.reduce((a, d) => a + (d.income || 0), 0) / last30.length;
      const perSpace = dailyAvg / capacity;
      result.push({
        icon: "local_parking",
        color: "info",
        text: `Cada cochera te deja ${fmt(perSpace)} por día (sobre ${capacity} cocheras cargadas)`,
      });
    }

    // Cancellation rate
    const canceled30 = last30.reduce((a, d) => a + (d.canceled_count || 0), 0);
    const tickets30 = last30.reduce((a, d) => a + (d.tickets?.total?.in || 0), 0);
    const rate = tickets30 > 0 ? (canceled30 / tickets30) * 100 : 0;
    if (rate > 5) {
      result.push({
        icon: "warning",
        color: "warning",
        text: `Se anula el ${rate.toFixed(1)}% de los tickets: ${canceled30} anulados en los últimos 30 días`,
      });
    }

    // Streak: consecutive days above/below average
    if (last30.length >= 7) {
      const dailyAvg = last30.reduce((a, d) => a + (d.income || 0), 0) / last30.length;
      let streak = 0;
      const firstAbove = (last30[0]?.income || 0) >= dailyAvg;
      for (const d of last30) {
        const above = (d.income || 0) >= dailyAvg;
        if (above === firstAbove) streak++;
        else break;
      }
      if (streak >= 3) {
        result.push({
          icon: firstAbove ? "local_fire_department" : "ac_unit",
          color: firstAbove ? "success" : "info",
          text: firstAbove
            ? `Racha positiva: ${streak} días seguidos por encima de tu promedio diario (${fmt(dailyAvg)})`
            : `${streak} días seguidos por debajo de tu promedio diario (${fmt(dailyAvg)})`,
        });
      }
    }

    // Best single day
    if (last30.length > 0) {
      const best = last30.reduce((a, b) => (a.income || 0) > (b.income || 0) ? a : b);
      result.push({
        icon: "star",
        color: "primary",
        text: `Tu mejor día reciente: ${moment(best.date).format("dddd DD/MM")} con ${fmt(best.income)}`,
      });
    }

    return result;
  }, [dailyDocs, liveData]);

  if (!insights.length) return null;

  return (
    <Card className="shadow-sm">
      <CardBody className="py-3">
        <h6 className="mb-3" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>lightbulb</span>
          Lo que salta a la vista
        </h6>
        <div className="d-flex flex-column gap-2">
          {insights.map((ins, i) => (
            <div key={i} className="d-flex align-items-start gap-2">
              <span className={`material-symbols-rounded text-${ins.color}`} style={{ fontSize: 18, marginTop: 1 }}>
                {ins.icon}
              </span>
              <span style={{ fontSize: 13 }}>{ins.text}</span>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

// ── Income by day of week ────────────────────────────────────
// Los tres colores de este gráfico SIGNIFICAN algo, así que se nombran una vez
// y se usan en las barras y en la referencia de abajo desde la misma constante:
// si alguien cambia el tono, la referencia lo sigue sola.
//
// Las siete barras son lo mismo —lo que deja un día—, así que no llevan tres
// identidades distintas sino el mismo tono graduado: el día más fuerte más
// oscuro, el más flojo más claro. En verde y rojo, un martes flojo parecía una
// pérdida, que es la lectura de "entra plata / sale plata" aplicada a algo que
// no tiene signo.
const DIA_MEJOR = INTENSIDAD.fuerte;
const DIA_PEOR = INTENSIDAD.flojo;
const DIA_RESTO = INTENSIDAD.normal;

const IncomeByDayOfWeek = ({ dailyDocs, heightOverride }) => {
  const data = useMemo(() => {
    const last30 = dailyDocs.slice(0, 30);
    if (last30.length < 7) return null;

    const buckets = { 1: [], 2: [], 3: [], 4: [], 5: [], 6: [], 7: [] };
    last30.forEach((d) => {
      const dow = moment(d.date).isoWeekday();
      buckets[dow].push(d.income || 0);
    });

    const labels = [];
    const values = [];
    const colors = [];
    for (let i = 1; i <= 7; i++) {
      labels.push(moment().isoWeekday(i).format("ddd"));
      const avg = buckets[i].length > 0
        ? buckets[i].reduce((a, b) => a + b, 0) / buckets[i].length
        : 0;
      values.push(Math.round(avg));
    }

    const max = Math.max(...values);
    values.forEach((v) => {
      if (v === max) colors.push(DIA_MEJOR);
      else if (v === Math.min(...values.filter(x => x > 0))) colors.push(DIA_PEOR);
      else colors.push(DIA_RESTO);
    });

    return { labels, values, colors };
  }, [dailyDocs]);

  if (!data) return null;

  const options = {
    chart: { type: "bar", height: heightOverride || 220, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
    plotOptions: { bar: { columnWidth: "55%", borderRadius: 4, distributed: true } },
    colors: data.colors,
    dataLabels: { enabled: false },
    xaxis: { categories: data.labels, labels: { style: { fontSize: "11px", textTransform: "capitalize" } } },
    yaxis: { labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
    // La leyenda de ApexCharts no sirve acá: con `distributed: true` arma una
    // entrada por CATEGORÍA —Lun, Mar, Mié…— que ya está escrita en el eje, y
    // no dice nada del significado del color. Los tres tonos de azul quieren
    // decir algo, así que se explican abajo con <Referencia>.
    legend: SIN_LEYENDA,
    grid: { borderColor: "#f1f1f1" },
  };

  return (
    <Card className="shadow-sm h-100">
      <CardBody className="py-2">
        <h6 className="mb-1" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>calendar_view_week</span>
          Lo que te deja cada día de la semana
        </h6>
        <ReactApexChart options={options} series={[{ data: data.values }]} type="bar" height={heightOverride || 220} />
        {/* Sin esto, la barra más oscura y la más clara se leen como
            decoración. Los tres tonos salen de INTENSIDAD, así que la
            referencia y las barras no se pueden desincronizar. */}
        <Referencia
          items={[
            data.colors.includes(DIA_MEJOR) && { color: DIA_MEJOR, texto: "El día que más te deja" },
            data.colors.includes(DIA_PEOR) && { color: DIA_PEOR, texto: "El que menos te deja" },
            data.colors.includes(DIA_RESTO) && { color: DIA_RESTO, texto: "El resto de los días" },
          ]}
        />
      </CardBody>
    </Card>
  );
};

// ── Ticket type distribution (donut) ────────────────────────
const TicketTypeDistribution = ({ dailyDocs }) => {
  const { labels, series, colors } = useMemo(() => {
    const last30 = dailyDocs.slice(0, 30);
    const hour = last30.reduce((a, d) => a + (d.tickets?.hour?.out || 0), 0);
    const stay = last30.reduce((a, d) => a + (d.tickets?.stay?.out || 0), 0);
    const tn = last30.reduce((a, d) => a + (d.tickets?.tn?.out || 0), 0);
    const plana = last30.reduce((a, d) => a + (d.tickets?.plana?.out || 0), 0);

    const items = [
      // Las cuatro son modalidades de cobro del ticket, o sea la misma familia
      // subdividida por duración: van en la rampa del azul de Tickets, de más
      // clara a más oscura, y no en cuatro tonos sueltos.
      { label: "Por hora", value: hour, color: MODALIDAD.hour.color },
      { label: "Por estadía", value: stay, color: MODALIDAD.stay.color },
      { label: "Por turno", value: tn, color: MODALIDAD.tn.color },
      { label: "Tarifa plana", value: plana, color: MODALIDAD.plana.color },
    ].filter((i) => i.value > 0);

    return {
      labels: items.map((i) => i.label),
      series: items.map((i) => i.value),
      colors: items.map((i) => i.color),
    };
  }, [dailyDocs]);

  if (!series.length) return null;

  const options = {
    chart: { type: "donut", fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
    labels,
    colors,
    // Las cuatro modalidades de cobro no están en ningún eje: la leyenda es lo
    // único que nombra cada porción.
    legend: LEYENDA_DONA,
    plotOptions: {
      pie: {
        donut: {
          size: "60%",
          labels: {
            show: true,
            value: { fontSize: "13px", fontWeight: 600 },
            total: { show: true, label: "Total", fontSize: "11px" },
          },
        },
      },
    },
    dataLabels: { enabled: true, formatter: (val) => `${val.toFixed(0)}%`, style: { fontSize: "11px" } },
    tooltip: { y: { formatter: (v) => `${v} tickets` } },
  };

  return (
    <Card className="shadow-sm h-100">
      <CardBody className="py-2">
        <h6 className="mb-1" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>confirmation_number</span>
          Cómo se cobró
          <span className="ms-1" style={TITULO_ACLARACION}>
            (últimos 30 días)
          </span>
        </h6>
        <ReactApexChart options={options} series={series} type="donut" height={230} />
      </CardBody>
    </Card>
  );
};

// ── Top payment methods (horizontal bar) ────────────────────
const TopPaymentMethods = ({ dailyDocs, heightOverride }) => {
  const data = useMemo(() => {
    const last30 = dailyDocs.slice(0, 30);
    const agg = {};
    last30.forEach((d) => {
      if (d.payments && typeof d.payments === "object") {
        Object.entries(d.payments).forEach(([method, val]) => {
          agg[method] = (agg[method] || 0) + (val || 0);
        });
      }
    });
    const sorted = Object.entries(agg)
      .filter(([, v]) => v > 0)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    return sorted;
  }, [dailyDocs]);

  // La paleta se arma con los medios que se van a dibujar, así que dos medios
  // distintos no pueden salir del mismo color en el mismo gráfico.
  const colorDePago = useMemo(() => paletaLibre(data.map(([nombre]) => nombre)), [data]);

  if (!data.length) return null;

  const options = {
    chart: { type: "bar", height: heightOverride || 220, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
    // `distributed` para que el color salga del NOMBRE del medio de pago y no
    // de su puesto en el ranking: el mismo "Efectivo" es del mismo color acá y
    // en la dona de la pestaña Ingresos, que es el otro lugar donde el dueño lo
    // busca.
    plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: "55%", distributed: true } },
    colors: data.map(([nombre]) => colorDePago(nombre)),
    dataLabels: { enabled: false },
    xaxis: { categories: data.map(([k]) => k), labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } } },
    yaxis: { labels: { style: { fontSize: "11px" } } },
    tooltip: { y: { formatter: (v) => formatCurrency(v, true) } },
    // Cada barra lleva el nombre del medio de pago al lado, en el eje: una
    // leyenda repetiría lo que ya está escrito.
    legend: SIN_LEYENDA,
    grid: { borderColor: "#f1f1f1" },
  };

  return (
    <Card className="shadow-sm h-100">
      <CardBody className="py-2">
        <h6 className="mb-1" style={TITULO_CARD}>
          <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>credit_card</span>
          Cómo te pagan
          <span className="ms-1" style={TITULO_ACLARACION}>
            (últimos 30 días)
          </span>
        </h6>
        <ReactApexChart
          options={options}
          // "Total" no dice total de qué. Este rótulo encabeza el tooltip.
          series={[{ name: "Cobrado", data: data.map(([, v]) => v) }]}
          type="bar"
          height={heightOverride || 220}
        />
      </CardBody>
    </Card>
  );
};

// ── Chart type options ────────────────────────────────────────
const CHART_TYPES = [
  { key: "area", label: "Área", icon: "area_chart" },
  { key: "bar", label: "Barras", icon: "bar_chart" },
  { key: "line", label: "Línea", icon: "show_chart" },
];

// ── Mini income chart ────────────────────────────────────────
const MiniIncomeChart = ({ dailyDocs, heightOverride }) => {
  const [chartType, setChartType] = useState("area");

  const last14 = useMemo(
    () => [...dailyDocs].slice(0, 14).reverse(),
    [dailyDocs]
  );

  const hayDiaEnCurso = useMemo(() => last14.some(esDiaEnCurso), [last14]);

  const options = useMemo(
    () => ({
      chart: { type: chartType, height: heightOverride || 220, ...TOOLBAR_DESCARGA, fontFamily: "inherit", animations: { dynamicAnimation: { enabled: false } } },
      colors: [POLARIDAD.entra.color],
      stroke: { curve: "smooth", width: chartType === "bar" ? 0 : 2.5 },
      fill: chartType === "area"
        ? { type: "gradient", gradient: { shadeIntensity: 1, opacityFrom: 0.4, opacityTo: 0.05 } }
        : chartType === "bar"
          ? { opacity: 0.85 }
          : { opacity: 1 },
      plotOptions: chartType === "bar" ? {
        bar: { borderRadius: 4, columnWidth: "55%" },
      } : {},
      xaxis: {
        // El día en curso se marca en el eje: es parcial y no se puede comparar
        // de igual a igual con los días cerrados que tiene al lado.
        categories: last14.map((d) =>
          esDiaEnCurso(d)
            ? `${moment(d.date).format("DD/MM")} (hoy)`
            : moment(d.date).format("DD/MM")
        ),
        labels: { style: { fontSize: "10px" } },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: { formatter: (v) => formatCurrency(v), style: { fontSize: "10px" } },
      },
      // El último punto queda hueco y más grande cuando es el día en curso.
      markers: hayDiaEnCurso
        ? {
            size: last14.map((d) => (esDiaEnCurso(d) ? 5 : 0)),
            strokeColors: POLARIDAD.entra.color,
            strokeWidth: 2,
            colors: ["#ffffff"],
          }
        : { size: 0 },
      tooltip: {
        y: {
          formatter: (v, opts) => {
            const d = last14[opts?.dataPointIndex];
            const base = formatCurrency(v, true);
            return esDiaEnCurso(d) ? `${base} (parcial, el día no cerró)` : base;
          },
        },
      },
      // Una sola serie: el título ya dice que son los ingresos. El único otro
      // signo del gráfico —el punto hueco del día de hoy— se explica con la
      // línea de texto que va debajo, no con un cuadradito de color.
      legend: SIN_LEYENDA,
      grid: { borderColor: "#f1f1f1", padding: { left: 5, right: 5 } },
      dataLabels: { enabled: false },
    }),
    [last14, chartType, hayDiaEnCurso]
  );

  const series = useMemo(
    () => [{ name: "Ingresos", data: last14.map((d) => d.income || 0) }],
    [last14]
  );

  if (last14.length < 3) return null;

  return (
    <Card className="shadow-sm">
      <CardBody className="py-2">
        <div className="d-flex align-items-center justify-content-between mb-1">
          <h6 className="mb-0" style={TITULO_CARD}>
            <span className="material-symbols-rounded me-1" style={{ fontSize: 16, verticalAlign: "middle" }}>trending_up</span>
            Cómo vino la plata
            <span className="ms-1" style={TITULO_ACLARACION}>
              (últimos 14 días)
            </span>
          </h6>
          <ButtonGroup size="sm">
            {CHART_TYPES.map((ct) => (
              <Button
                key={ct.key}
                color={chartType === ct.key ? "primary" : "light"}
                onClick={() => setChartType(ct.key)}
                title={ct.label}
                style={{ padding: "2px 8px", lineHeight: 1 }}
              >
                <span className="material-symbols-rounded" style={{ fontSize: 16 }}>{ct.icon}</span>
              </Button>
            ))}
          </ButtonGroup>
        </div>
        <ReactApexChart key={chartType} options={options} series={series} type={chartType} height={heightOverride || 220} />
        {hayDiaEnCurso && (
          <div className="text-muted text-end" style={{ fontSize: 11 }}>
            El último punto es el día de hoy y todavía está en curso.
          </div>
        )}
      </CardBody>
    </Card>
  );
};

// ── Tab principal ────────────────────────────────────────────
const TabResumen = ({ dailyDocs, monthlyDocs, liveData }) => {
  const hasCategories = useCategories(dailyDocs.slice(0, 30));
  const colSize = hasCategories ? 3 : 4;

  return (
    <>
      <KpiCards dailyDocs={dailyDocs} liveData={liveData} />

      <Row className="g-3 mt-1">
        <Col xl={8}>
          <ExpandableChart title="Cómo vino la plata">
            <MiniIncomeChart dailyDocs={dailyDocs} />
          </ExpandableChart>
        </Col>
        <Col xl={4}>
          <Insights dailyDocs={dailyDocs} liveData={liveData} />
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xl={colSize}>
          <ExpandableChart title="Lo que te deja cada día de la semana">
            <IncomeByDayOfWeek dailyDocs={dailyDocs} />
          </ExpandableChart>
        </Col>
        <Col xl={colSize}>
          <TicketTypeDistribution dailyDocs={dailyDocs} />
        </Col>
        {hasCategories && (
          <Col xl={3}>
            <CategorySummary docs={dailyDocs.slice(0, 30)} />
          </Col>
        )}
        <Col xl={colSize}>
          <ExpandableChart title="Cómo te pagan">
            <TopPaymentMethods dailyDocs={dailyDocs} />
          </ExpandableChart>
        </Col>
      </Row>

      {dailyDocs.length >= 2 && (
        <Row className="g-3 mt-1">
          <Col xl={12}>
            <PeriodComparison dailyDocs={dailyDocs} monthlyDocs={monthlyDocs} liveData={liveData} />
          </Col>
        </Row>
      )}
    </>
  );
};

export default TabResumen;
